import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useHasPermission } from "@/shared/rbac";
import { type ProvizniPasmo, provizniPasmaSchema } from "@template/shared";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const KC_FMT = new Intl.NumberFormat("cs-CZ", {
  style: "currency",
  currency: "CZK",
  maximumFractionDigits: 2,
});

const DATE_FMT = new Intl.DateTimeFormat("cs-CZ", { dateStyle: "medium", timeStyle: "short" });

/** Editovatelný řádek pásma — hodnoty jako stringy kvůli inputům, `id` jen pro React key. */
interface PasmoRow {
  id: string;
  doKc: string;
  pct: string;
}

/** "199999,99" i "199999.99" → number; prázdné/nečíselné = NaN. */
function parseKc(value: string): number {
  if (value.trim() === "") return Number.NaN;
  return Number(value.replace(",", "."));
}

function pasmaToRows(pasma: readonly ProvizniPasmo[]): PasmoRow[] {
  return pasma.map((p) => ({
    id: crypto.randomUUID(),
    doKc: p.doKc !== null ? String(p.doKc) : "",
    pct: String(p.pct),
  }));
}

/**
 * Administrace → Nástroje → Provizní pásma OBCHODNÍKŮ. Měsíční žebřík
 * „objem do X Kč → Y %" pro výpočet provizí (stránka Provize → Můj měsíc).
 * Poslední řádek je vždy otevřený („a více"). Lektorská pásma se doplní,
 * až majitel nadefinuje lektorský model.
 */
export function ProvizniPasmaCard() {
  const utils = trpc.useUtils();
  const canWrite = useHasPermission("settings.write");
  const query = trpc.admin.settings.getProvizniPasma.useQuery();

  // null = ještě neinicializováno z query. Inicializuje se JEDNOU (ref) —
  // efekt nesmí po uložení přepsat formulář starou cache (invalidate
  // doběhne až později a rows by se naplnily neaktuální hodnotou).
  const [rows, setRows] = useState<PasmoRow[] | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!query.data || initialized.current) return;
    initialized.current = true;
    setRows(pasmaToRows(query.data.pasma));
  }, [query.data]);

  const mutation = trpc.admin.settings.setProvizniPasma.useMutation({
    onSuccess: (_data, variables) => {
      toast.success("Provizní pásma uložena");
      // Formulář rovnou z ULOŽENÝCH hodnot (variables mutace) — návrat ke
      // staré cache by po savu ukázal předchozí pásma. Invalidate jen
      // dotáhne metadata (updatedAt/updatedBy) a srovná cache se serverem.
      setRows(pasmaToRows(variables.pasma));
      utils.admin.settings.getProvizniPasma.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  if (query.isPending) {
    return <Skeleton className="h-64 w-full" data-testid="admin-provize-pasma-loading" />;
  }
  if (query.isError) {
    return (
      <div
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
        data-testid="admin-provize-pasma-error"
      >
        {query.error.message}
      </div>
    );
  }

  const effectiveRows = rows ?? pasmaToRows(query.data.pasma);
  const lastIndex = effectiveRows.length - 1;

  const updateRow = (index: number, patch: Partial<PasmoRow>) => {
    setValidationError(null);
    setRows(effectiveRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setValidationError(null);
    // Nové pásmo se vkládá PŘED poslední (otevřený) řádek.
    const next = [...effectiveRows];
    next.splice(lastIndex, 0, { id: crypto.randomUUID(), doKc: "", pct: "" });
    setRows(next);
  };

  const removeRow = (index: number) => {
    setValidationError(null);
    setRows(effectiveRows.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const pasma: ProvizniPasmo[] = effectiveRows.map((r, i) => ({
      doKc: i === lastIndex ? null : parseKc(r.doKc),
      pct: parseKc(r.pct),
    }));
    const parsed = provizniPasmaSchema.safeParse(pasma);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? issue.message : "Pásma nejsou validní");
      return;
    }
    setValidationError(null);
    mutation.mutate({ pasma: parsed.data });
  };

  const { source, updatedAt, updatedBy } = query.data;

  return (
    <Card data-testid="admin-provize-pasma-card">
      <CardContent className="space-y-4 p-6">
        <div className="space-y-1">
          <h2 className="font-semibold text-lg">Provizní pásma — obchodníci</h2>
          <p className="text-muted-foreground text-sm">
            Měsíční žebřík provize podle objemu uzavřených seminářů obchodníka. Objem do hranice
            včetně spadá do daného pásma; poslední řádek platí pro vše nad poslední hranicí.
            Lektorská pásma budou doplněna, až majitel nadefinuje lektorský model.
          </p>
          <p className="text-muted-foreground text-xs">
            {source === "db"
              ? `Uloženo v administraci${updatedAt ? ` · ${DATE_FMT.format(new Date(updatedAt))}` : ""}${updatedBy ? ` · ${updatedBy}` : ""}`
              : "Platí výchozí hodnoty (zatím nikdo neuložil vlastní pásma)."}
          </p>
        </div>

        <div className="space-y-2" data-testid="admin-provize-pasma-table">
          <div className="grid grid-cols-[1fr_7rem_2.5rem] items-end gap-2 text-muted-foreground text-xs">
            <span>Objem do (Kč, včetně)</span>
            <span>Provize %</span>
            <span aria-hidden="true" />
          </div>

          {effectiveRows.map((row, i) => {
            const isLast = i === lastIndex;
            return (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_7rem_2.5rem] items-center gap-2"
                data-testid={`admin-provize-pasma-row-${i}`}
              >
                {isLast ? (
                  <span className="px-3 text-muted-foreground text-sm italic">
                    a více (nad poslední hranici)
                  </span>
                ) : (
                  <div>
                    <Label htmlFor={`provize-pasmo-do-${i}`} className="sr-only">
                      Horní hranice pásma {i + 1} v Kč
                    </Label>
                    <Input
                      id={`provize-pasmo-do-${i}`}
                      type="number"
                      min={0}
                      step={0.01}
                      inputMode="decimal"
                      placeholder="199999,99"
                      value={row.doKc}
                      onChange={(e) => updateRow(i, { doKc: e.target.value })}
                      disabled={!canWrite}
                      data-testid={`admin-provize-pasma-do-input-${i}`}
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor={`provize-pasmo-pct-${i}`} className="sr-only">
                    Provizní procento pásma {i + 1}
                  </Label>
                  <Input
                    id={`provize-pasmo-pct-${i}`}
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    inputMode="decimal"
                    value={row.pct}
                    onChange={(e) => updateRow(i, { pct: e.target.value })}
                    disabled={!canWrite}
                    data-testid={`admin-provize-pasma-pct-input-${i}`}
                  />
                </div>
                {isLast ? (
                  <span aria-hidden="true" />
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRow(i)}
                    disabled={!canWrite}
                    aria-label={`Smazat pásmo ${i + 1}`}
                    data-testid={`admin-provize-pasma-remove-${i}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {validationError !== null ? (
          <p
            className="text-destructive text-sm"
            role="alert"
            data-testid="admin-provize-pasma-validation-error"
          >
            {validationError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={addRow}
            disabled={!canWrite}
            data-testid="admin-provize-pasma-add"
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            Přidat pásmo
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canWrite || mutation.isPending}
            data-testid="admin-provize-pasma-save"
          >
            {mutation.isPending ? "Ukládám…" : "Uložit pásma"}
          </Button>
          <span className="text-muted-foreground text-xs">
            Příklad: objem {KC_FMT.format(199_999.99)} → první pásmo, {KC_FMT.format(200_000)} →
            druhé pásmo.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
